import type { SxProps, Theme } from '@mui/material';
import { Color } from 'shared/utils/colors';

/**
 * Outlined treatment for a secondary text action (e.g. "Edit", "Archive",
 * "Expand all") so it reads as an interactive control instead of plain text
 * blending into the page background. Pairs with the contained primary CTA.
 * Apply to `ButtonTextIcon`.
 *
 * Uses the neutral `divider` (gray) border so secondary actions stay visually
 * consistent with the segmented `actionToolbarSx` toolbar across the app.
 */
export const secondaryActionButtonSx: SxProps<Theme> = {
  color: 'text.primary',
  borderStyle: 'solid',
  // Override the global `borderWidth: 2px !important` — 2px reads too heavy for a secondary action.
  borderWidth: '1px !important',
  // Gray border matching the toolbar/divider keyline; stays subtle when disabled.
  borderColor: 'divider',
  // Match the contained CTA radius (theme default ~4px); ButtonTextIcon otherwise forces 2px.
  borderRadius: '4px !important',
  whiteSpace: 'nowrap',
  '&.Mui-disabled': {
    borderColor: 'divider',
  },
};

/**
 * Segmented toolbar that groups several secondary text actions (e.g. "Edit",
 * "Archive", "Export to PDF") into a single bordered control with vertical
 * dividers between buttons. Wrap a row of `ButtonTextIcon`s with it so the
 * actions read as one cohesive toolbar instead of loose buttons. Shared across
 * the single issue, single inspection and single template headers.
 */
export const actionToolbarSx: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'stretch',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  overflow: 'hidden',
  backgroundColor: 'background.paper',
  flexShrink: 0,
  '& > *': {
    display: 'inline-flex',
    alignItems: 'center',
  },
  '& > *:not(:last-child)': {
    borderRight: '1px solid',
    borderColor: 'divider',
  },
  '& .MuiButtonBase-root': {
    borderRadius: 0,
    height: '100%',
  },
};

/**
 * In-context "add" action button (e.g. "+ Add answer", "+ Add option").
 * Transparent background with brand-yellow hover tint.
 * Use for inline creation actions within forms/panels.
 */
export const inContextAddButtonSx: SxProps<Theme> = {
  ...secondaryActionButtonSx,
  textTransform: 'none',
  fontSize: '1rem',
  fontWeight: 600,
  bgcolor: 'transparent',
  borderColor: Color.border,
  transition: 'background-color 0.15s ease-in-out',
  '&:hover': {
    bgcolor: Color.primaryOrangeHover,
    borderColor: Color.border,
  },
  '&.Mui-disabled': {
    borderColor: Color.border,
    color: Color.mutedBlack,
  },
};
